<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  stats: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();
const INTENT_SOURCES = ['jev', 'llm_fallback', 'none'];

const routeChips = computed(() =>
  (props.stats ? Object.entries(props.stats.by_route || {}) : []).map(
    ([route, count]) => ({ route, label: `${route} · ${count}` })
  )
);

const answeredByChips = computed(() =>
  (props.stats ? Object.entries(props.stats.by_answered_by || {}) : []).map(
    ([source, count]) => ({
      source,
      label: `${INTENT_SOURCES.includes(source) ? t(`AI_AGENT.INTENT.SOURCES.${source.toUpperCase()}`) : source} · ${count}`,
    })
  )
);

const appliedPercent = computed(() =>
  props.stats ? Math.round((props.stats.applied_rate || 0) * 100) : 0
);

const appliedLabel = computed(() => `${appliedPercent.value}%`);

const avgLatencyLabel = computed(
  () => `${props.stats?.avg_latency_ms ?? 0} ms`
);
</script>

<template>
  <div
    v-if="stats"
    class="flex flex-wrap gap-4 rounded-lg bg-n-solid-2 p-3"
    data-testid="intent-stats"
  >
    <div class="flex min-w-40 flex-col gap-1">
      <span class="text-body-mini text-n-slate-10">
        {{ t('AI_AGENT.INTENT.STATS.BY_ROUTE') }}
      </span>
      <div class="flex flex-wrap gap-2">
        <span
          v-for="chip in routeChips"
          :key="chip.route"
          class="rounded-md bg-n-brand/10 px-2 py-0.5 text-body-mini text-n-brand"
        >
          {{ chip.label }}
        </span>
        <span v-if="!routeChips.length" class="text-body-mini text-n-slate-10">
          {{ t('AI_AGENT.INTENT.STATS.NO_DATA') }}
        </span>
      </div>
    </div>
    <div class="flex min-w-32 flex-col gap-1">
      <span class="text-body-mini text-n-slate-10">
        {{ t('AI_AGENT.INTENT.STATS.BY_ANSWERED_BY') }}
      </span>
      <div class="flex flex-wrap gap-2">
        <span
          v-for="chip in answeredByChips"
          :key="chip.source"
          class="rounded-md bg-n-slate-4 px-2 py-0.5 text-body-mini text-n-slate-11"
        >
          {{ chip.label }}
        </span>
      </div>
    </div>
    <div class="flex min-w-24 flex-col gap-1">
      <span class="text-body-mini text-n-slate-10">
        {{ t('AI_AGENT.INTENT.STATS.LOST') }}
      </span>
      <span class="text-body-main text-n-ruby-11">{{ stats.lost }}</span>
    </div>
    <div class="flex min-w-24 flex-col gap-1">
      <span class="text-body-mini text-n-slate-10">
        {{ t('AI_AGENT.INTENT.STATS.APPLIED_RATE') }}
      </span>
      <span class="text-body-main text-n-slate-12">
        {{ appliedLabel }}
      </span>
    </div>
    <div class="flex min-w-24 flex-col gap-1">
      <span class="text-body-mini text-n-slate-10">
        {{ t('AI_AGENT.INTENT.STATS.AVG_LATENCY') }}
      </span>
      <span class="text-body-main text-n-slate-12">
        {{ avgLatencyLabel }}
      </span>
    </div>
  </div>
</template>
